import React, { FormEvent, useEffect, useMemo, useState } from 'react';
import { BellIcon, CheckCircle2Icon, ChevronDownIcon, MailIcon, MessageCircleIcon, PaperclipIcon, PlusIcon, SendHorizontalIcon, SlidersHorizontalIcon, UserRoundIcon, XIcon } from 'lucide-react';
type MessageCategory = 'Action needed' | 'Update' | 'Conversation';
type MessageAuthor = 'ManyCo' | 'Brand';
type InboxSort = 'Recent' | 'Waiting for reply';
type ThreadMessage = {
  id: number;
  author: MessageAuthor;
  authorName: string;
  timestamp: string;
  text: string;
};
type BrandThread = {
  id: string;
  brandId: string;
  title: string;
  category: MessageCategory;
  preview: string;
  updatedAt: string;
  unread: boolean;
  waitingForReply: boolean;
  messages: ThreadMessage[];
};
type BrandAccount = {
  id: string;
  name: string;
  assignedRep: string;
  coverageRep: string;
  coverageNote?: string;
};
type Contact = {
  id: string;
  name: string;
  role: string;
  initials: string;
  available: boolean;
  tone: string;
};
const contacts: Contact[] = [{
  id: 'meris',
  name: 'Meris',
  role: 'Head of Partnerships',
  initials: 'MP',
  available: false,
  tone: 'bg-[#A77ACA]'
}, {
  id: 'meg',
  name: 'Meg',
  role: 'Sales representative',
  initials: 'MG',
  available: true,
  tone: 'bg-[#C97968]'
}, {
  id: 'service',
  name: 'Customer service',
  role: 'Operations support',
  initials: 'CS',
  available: true,
  tone: 'bg-[#4D9CC7]'
}];
export const brands: BrandAccount[] = [{
  id: 'eddy',
  name: 'Eddy',
  assignedRep: 'meris',
  coverageRep: 'meg',
  coverageNote: 'Meris is away · Meg is covering'
}, {
  id: 'one-people',
  name: '1 People',
  assignedRep: 'meg',
  coverageRep: 'meg'
}, {
  id: 'gap',
  name: 'GAP',
  assignedRep: 'meris',
  coverageRep: 'meris'
}, {
  id: 'nature-baby',
  name: 'Nature Baby',
  assignedRep: 'meg',
  coverageRep: 'meg'
}];
export const initialThreads: BrandThread[] = [{
  id: 'eddy-plan',
  brandId: 'eddy',
  title: 'Approval needed: Summer recovery plan',
  category: 'Action needed',
  preview: 'Your proposed pricing and channel plan is ready to review.',
  updatedAt: '12m',
  unread: true,
  waitingForReply: false,
  messages: [{
    id: 1,
    author: 'ManyCo',
    authorName: 'Meg · ManyCo',
    timestamp: 'Today, 9:42 AM',
    text: 'We completed the summer recovery plan using current inventory, channel rules, and pricing guardrails. Please review the proposed channel mix and approve when ready.'
  }, {
    id: 2,
    author: 'ManyCo',
    authorName: 'Carli',
    timestamp: 'Today, 9:43 AM',
    text: 'The recommendation prioritizes 142 units with the strongest recovery opportunity. No items will be published until your approval is recorded.'
  }]
}, {
  id: 'eddy-question',
  brandId: 'eddy',
  title: 'Question about approved channels',
  category: 'Conversation',
  preview: 'Can we keep the capsule collection off marketplace channels?',
  updatedAt: '43m',
  unread: true,
  waitingForReply: true,
  messages: [{
    id: 3,
    author: 'Brand',
    authorName: 'Eddy team',
    timestamp: 'Today, 9:12 AM',
    text: 'Can we keep the capsule collection off marketplace channels while still including the core assortment?'
  }]
}, {
  id: 'eddy-inventory',
  brandId: 'eddy',
  title: 'Inventory file received',
  category: 'Update',
  preview: 'Your June inventory export has been added to the recovery workspace.',
  updatedAt: 'Yesterday',
  unread: false,
  waitingForReply: false,
  messages: [{
    id: 4,
    author: 'ManyCo',
    authorName: 'ManyCo operations',
    timestamp: 'Yesterday, 3:18 PM',
    text: 'Your June inventory export has been received and is now available in Inventory. Carli will use it in the next recovery review.'
  }]
}, {
  id: 'people-pricing',
  brandId: 'one-people',
  title: 'Request to review floor pricing',
  category: 'Conversation',
  preview: 'Could we revisit the approved floor for outerwear?',
  updatedAt: '1h',
  unread: true,
  waitingForReply: true,
  messages: [{
    id: 5,
    author: 'Brand',
    authorName: '1 People team',
    timestamp: 'Today, 8:36 AM',
    text: 'Could we revisit the approved floor price for the outerwear assortment before the next publishing window?'
  }]
}, {
  id: 'gap-recap',
  brandId: 'gap',
  title: 'May recovery recap',
  category: 'Update',
  preview: 'A summary of recovered value, sell-through, and active channels.',
  updatedAt: 'May 31',
  unread: false,
  waitingForReply: false,
  messages: [{
    id: 6,
    author: 'ManyCo',
    authorName: 'Meris · ManyCo',
    timestamp: 'May 31, 4:00 PM',
    text: 'Your May recovery recap is ready. You can see recovered value, sell-through, and channel activity in Performance and Finances.'
  }]
}, {
  id: 'nature-baby-submission',
  brandId: 'nature-baby',
  title: 'Inventory submission needs confirmation',
  category: 'Action needed',
  preview: 'Please confirm the unit counts on your latest submission.',
  updatedAt: '3h',
  unread: true,
  waitingForReply: true,
  messages: [{
    id: 7,
    author: 'Brand',
    authorName: 'Nature Baby team',
    timestamp: 'Today, 6:58 AM',
    text: 'We sent over our summer submission but the unit counts look different from our export. Can you confirm what was received?'
  }]
}];
const categories: Array<MessageCategory | 'All'> = ['All', 'Action needed', 'Update', 'Conversation'];
function getBrandIdForOwner(owner: string) {
  return brands.find(brand => brand.name.toLocaleLowerCase() === owner.trim().toLocaleLowerCase())?.id ?? null;
}
export function BrandMessagesDashboard({
  selectedOwner
}: {
  selectedOwner: string;
}) {
  const defaultBrandId = getBrandIdForOwner(selectedOwner) ?? 'eddy';
  const [threads, setThreads] = useState(initialThreads);
  const [selectedBrandId, setSelectedBrandId] = useState(defaultBrandId);
  const [activeId, setActiveId] = useState(initialThreads.find(thread => thread.brandId === defaultBrandId)?.id ?? 'eddy-plan');
  useEffect(() => {
    const brandId = getBrandIdForOwner(selectedOwner);
    if (!brandId) return;
    setSelectedBrandId(brandId);
    const firstThread = initialThreads.find(thread => thread.brandId === brandId);
    if (firstThread) setActiveId(firstThread.id);
  }, [selectedOwner]);
  const [category, setCategory] = useState<MessageCategory | 'All'>('All');
  const [sort, setSort] = useState<InboxSort>('Recent');
  const [showBrandMessages, setShowBrandMessages] = useState(true);
  const [brandPickerOpen, setBrandPickerOpen] = useState(false);
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [newMessageOpen, setNewMessageOpen] = useState(false);
  const [recipientId, setRecipientId] = useState('meg');
  const [subject, setSubject] = useState('');
  const [newMessage, setNewMessage] = useState('');
  const [reply, setReply] = useState('');
  const selectedBrand = brands.find(brand => brand.id === selectedBrandId) ?? brands[0];
  const currentContact = contacts.find(contact => contact.id === selectedBrand.coverageRep) ?? contacts[1];
  const waitingCount = threads.filter(thread => thread.waitingForReply).length;
  const activeThread = threads.find(thread => thread.id === activeId) ?? threads.find(thread => thread.brandId === selectedBrandId) ?? threads[0];
  const inboxThreads = useMemo(() => {
    const byBrand = threads.filter(thread => thread.brandId === selectedBrandId);
    const byVisibility = showBrandMessages ? byBrand : byBrand.filter(thread => thread.messages.every(message => message.author !== 'Brand'));
    const byCategory = category === 'All' ? byVisibility : byVisibility.filter(thread => thread.category === category);
    return [...byCategory].sort((first, second) => sort === 'Waiting for reply' ? Number(second.waitingForReply) - Number(first.waitingForReply) : 0);
  }, [category, selectedBrandId, showBrandMessages, sort, threads]);
  const selectBrand = (brand: BrandAccount) => {
    setSelectedBrandId(brand.id);
    setRecipientId(brand.coverageRep);
    setBrandPickerOpen(false);
    const first = threads.find(thread => thread.brandId === brand.id);
    if (first) setActiveId(first.id);
  };
  const toggleBrandMessages = () => {
    const next = !showBrandMessages;
    setShowBrandMessages(next);
    if (!next && activeThread.messages.some(message => message.author === 'Brand')) {
      const nextThread = threads.find(thread => thread.brandId === selectedBrandId && thread.messages.every(message => message.author !== 'Brand'));
      if (nextThread) setActiveId(nextThread.id);
    }
  };
  const openThread = (id: string) => {
    setActiveId(id);
    setThreads(current => current.map(thread => thread.id === id ? {
      ...thread,
      unread: false
    } : thread));
  };
  const sendReply = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const text = reply.trim();
    if (!text) return;
    const timestamp = Date.now();
    setThreads(current => current.map(thread => thread.id === activeThread.id ? {
      ...thread,
      preview: text,
      updatedAt: 'Just now',
      unread: false,
      waitingForReply: false,
      messages: [...thread.messages, {
        id: timestamp,
        author: 'ManyCo',
        authorName: 'Rich · ManyCo',
        timestamp: 'Just now',
        text
      }]
    } : thread));
    setReply('');
  };
  const startConversation = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const title = subject.trim();
    const text = newMessage.trim();
    if (!title || !text) return;
    const recipient = contacts.find(contact => contact.id === recipientId) ?? currentContact;
    const item: BrandThread = {
      id: `new-${Date.now()}`,
      brandId: selectedBrandId,
      title,
      category: 'Conversation',
      preview: text,
      updatedAt: 'Just now',
      unread: false,
      waitingForReply: false,
      messages: [{
        id: Date.now(),
        author: 'ManyCo',
        authorName: `Rich · ManyCo to ${recipient.name}`,
        timestamp: 'Just now',
        text
      }]
    };
    setThreads(current => [item, ...current]);
    setActiveId(item.id);
    setSubject('');
    setNewMessage('');
    setNewMessageOpen(false);
  };
  return <div className="mx-auto max-w-7xl px-5 py-6 sm:px-8 lg:py-8" aria-label="Brand messages" data-id="element-221" data-mp="r8etecn2j1q4">
    <section className="overflow-visible rounded-3xl border border-ink/10 bg-paper shadow-sm" data-id="element-222" data-mp="m67fhhiohbl5">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-ink/10 px-4 py-3 sm:px-5" data-id="element-223" data-mp="22kfp6bicnqfd">
        <div className="relative flex min-w-0 items-center gap-3" data-id="element-224" data-mp="1xmo0dqd04kfe">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#BB5E40]/10 text-[#BB5E40]" data-id="element-225" data-mp="2uf86jb9zovvz"><MessageCircleIcon className="h-4 w-4" data-id="element-226" data-mp="2pihq2mu9vya1" /></span>
          <div data-id="element-227" data-mp="2kq875qrkryh9"><p className="text-sm font-semibold" data-id="element-228" data-mp="2kpydnt7bn96t">Messages</p><p className="text-xs text-ink/50" data-id="element-229" data-mp="2kowybcj9w0ja">Admin workspace · {waitingCount} waiting for reply</p></div>
          <button type="button" onClick={() => setBrandPickerOpen(open => !open)} aria-expanded={brandPickerOpen} className="ml-1 inline-flex max-w-[180px] items-center gap-1.5 rounded-lg border border-ink/10 bg-cream/55 px-2.5 py-1.5 text-xs font-semibold text-ink/70 hover:border-[#BB5E40]/30" data-id="element-230" data-mp="2gomlph0z6bvg"><span className="truncate" data-id="element-231" data-mp="21s47dmxkvbs4">{selectedBrand.name}</span><ChevronDownIcon className={`h-3.5 w-3.5 shrink-0 transition-transform ${brandPickerOpen ? 'rotate-180' : ''}`} data-id="element-232" data-mp="1xqaalpqi6rx8" /></button>
          {brandPickerOpen && <div className="absolute left-0 top-[calc(100%+10px)] z-30 w-72 rounded-xl border border-ink/10 bg-paper p-1.5 shadow-xl" data-id="element-233" data-mp="3w4u38a7iav25">{brands.map(brand => {
              const waiting = threads.filter(thread => thread.brandId === brand.id && thread.waitingForReply).length;
              return <button key={brand.id} type="button" onClick={() => selectBrand(brand)} className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm ${brand.id === selectedBrandId ? 'bg-[#BB5E40]/10 text-ink' : 'text-ink/70 hover:bg-cream'}`} data-id="element-234" data-mp="1iuk884mrunlr"><span className="flex h-7 w-7 items-center justify-center rounded-full bg-ink/8 text-[10px] font-bold text-ink/60" data-id="element-235" data-mp="1rprv1ae34ro2">{brand.name.slice(0, 2).toUpperCase()}</span><span className="flex-1 font-semibold" data-id="element-236" data-mp="zxj0ic1u29e4">{brand.name}</span>{waiting > 0 && <span className="rounded-full bg-[#C9A04E] px-1.5 py-0.5 text-[10px] font-bold text-ink" data-id="element-237" data-mp="13ycmv0zmng4w">{waiting} waiting</span>}</button>;
            })}</div>}
        </div>
        <div className="flex items-center gap-2" data-id="element-238" data-mp="18yh6v0zquszv"><button type="button" onClick={() => setEmailNotifications(enabled => !enabled)} aria-pressed={emailNotifications} className={`inline-flex items-center gap-2 rounded-lg px-2.5 py-2 text-xs font-semibold ${emailNotifications ? 'bg-forest/8 text-forest' : 'text-ink/55 hover:bg-cream'}`} data-id="element-239" data-mp="2fdi3za0i9h81"><BellIcon className="h-3.5 w-3.5" data-id="element-240" data-mp="g8i3te24j94a" />Email {emailNotifications ? 'on' : 'off'}</button><button type="button" onClick={() => {
            setRecipientId(currentContact.id);
            setNewMessageOpen(true);
          }} className="inline-flex items-center gap-2 rounded-lg bg-[#BB5E40] px-3 py-2 text-xs font-semibold text-cream hover:bg-[#94452E]" data-id="element-241" data-mp="gaktty3a1290"><PlusIcon className="h-3.5 w-3.5" data-id="element-242" data-mp="1jc31lnn1aua" />Start message</button></div>
      </div>

      <div className="grid min-h-[640px] lg:grid-cols-[320px_minmax(0,1fr)]" data-id="element-243" data-mp="3spwtb35vceq9">
        <aside className="flex min-h-0 flex-col border-b border-ink/10 bg-cream/50 p-3 lg:border-b-0 lg:border-r" aria-label="Conversation list" data-id="element-244" data-mp="3sntt5qb0557t">
          <div className="rounded-xl border border-ink/10 bg-paper p-3" data-id="element-245" data-mp="6iezwitgsar6"><div className="flex items-center justify-between gap-3" data-id="element-246" data-mp="6gvjms7t5zpe"><div data-id="element-247" data-mp="3ex75ct6s4zqi"><p className="text-xs font-semibold" data-id="element-248" data-mp="3ex746pv1mtox">Brand-originated messages</p><p className="mt-0.5 text-[11px] text-ink/50" data-id="element-249" data-mp="39zx7qq98lhrl">Show messages brands sent to ManyCo.</p></div><Toggle checked={showBrandMessages} onChange={toggleBrandMessages} label="Toggle messages from brands" data-id="element-250" data-mp="1phmpczvkfvqy" /></div></div>
          <div className="mt-3 flex items-center justify-between gap-2" data-id="element-251" data-mp="1u94pxmtofube"><div className="flex gap-1" aria-label="Conversation categories" data-id="element-252" data-mp="1ggojsd5hbg69">{categories.map(item => <button key={item} type="button" onClick={() => setCategory(item)} className={`rounded-md px-2 py-1.5 text-[10px] font-semibold ${category === item ? 'bg-ink text-cream' : 'text-ink/55 hover:bg-paper'}`} data-id="element-253" data-mp="1gevbktf7wnw5">{item === 'Action needed' ? 'Action' : item}</button>)}</div><button type="button" onClick={() => setSort(current => current === 'Recent' ? 'Waiting for reply' : 'Recent')} className={`inline-flex shrink-0 items-center gap-1 rounded-md px-2 py-1.5 text-[10px] font-semibold ${sort === 'Waiting for reply' ? 'bg-[#C9A04E]/15 text-[#8B6A20]' : 'text-ink/55 hover:bg-paper'}`} data-id="element-254" data-mp="2d5npbkeoi3ur"><SlidersHorizontalIcon className="h-3 w-3" data-id="element-255" data-mp="nqpq24gcauv9" />{sort === 'Recent' ? 'Recent' : 'Waiting'}</button></div>
          <div className="mt-3 min-h-0 flex-1 space-y-1 overflow-y-auto" data-id="element-256" data-mp="sp0yqbuwezbw">{inboxThreads.map(thread => <button key={thread.id} type="button" onClick={() => openThread(thread.id)} className={`w-full rounded-xl p-3 text-left ${thread.id === activeThread.id ? 'bg-[#BB5E40]/10' : 'hover:bg-paper'}`} data-id="element-257" data-mp="182tuwn6qzyy9"><div className="flex gap-2" data-id="element-258" data-mp="yb79jy0p4jhs"><span className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full ${thread.waitingForReply ? 'bg-[#C9A04E]' : thread.category === 'Update' ? 'bg-forest' : 'bg-[#BB5E40]'}`} data-id="element-259" data-mp="u9cc54lvyq9a" /><div className="min-w-0 flex-1" data-id="element-260" data-mp="1qytq06v0vim2"><div className="flex items-start justify-between gap-2" data-id="element-261" data-mp="1m0ifdtwwl42s"><p className="line-clamp-2 text-xs font-semibold leading-snug text-ink" data-id="element-262" data-mp="1m1tgflyyrxq7">{thread.title}</p><span className="shrink-0 text-[10px] text-ink/40" data-id="element-263" data-mp="1hxvgj91teiw3">{thread.updatedAt}</span></div><p className="mt-1 line-clamp-2 text-[11px] leading-relaxed text-ink/55" data-id="element-264" data-mp="1d0mzjbsekkwf">{thread.preview}</p>{thread.waitingForReply && <span className="mt-2 inline-flex rounded-full bg-[#C9A04E]/15 px-1.5 py-0.5 text-[9px] font-semibold text-[#8B6A20]" data-id="element-265" data-mp="6kjbiuubju8c">Waiting for reply</span>}</div>{thread.unread && <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-[#BB5E40]" data-id="element-266" data-mp="2d8a8vqbk5pzo" />}</div></button>)}{inboxThreads.length === 0 && <p className="px-3 py-8 text-center text-xs text-ink/50" data-id="element-267" data-mp="2i6btifomtxd9">No conversations in this view.</p>}</div>
        </aside>

        <main className="flex min-w-0 flex-col" data-id="element-268" data-mp="2m75fh3iil23i"><header className="flex flex-wrap items-start justify-between gap-3 border-b border-ink/10 px-5 py-4 sm:px-7" data-id="element-269" data-mp="2qzezk2wy77xv"><div className="min-w-0" data-id="element-270" data-mp="2vyrxpjt9s1fr"><div className="flex flex-wrap items-center gap-2" data-id="element-271" data-mp="2vzb3y39k51dy"><h1 className="text-lg font-semibold tracking-tight" data-id="element-272" data-mp="2w0mbme64itac">{activeThread.title}</h1><CategoryPill category={activeThread.category} data-id="element-273" data-mp="30w1ksst6s10f" /></div><p className="mt-1 text-xs text-ink/50" data-id="element-274" data-mp="35w62un9qt3sr">{selectedBrand.name} · Private conversation with ManyCo</p></div>{activeThread.waitingForReply ? <span className="inline-flex items-center gap-1.5 rounded-full bg-[#C9A04E]/15 px-2.5 py-1 text-[11px] font-semibold text-[#8B6A20]" data-id="element-275" data-mp="39wzn96nz80gw"><BellIcon className="h-3 w-3" data-id="element-276" data-mp="1bl12psxv79i6" />Needs reply</span> : <span className="inline-flex items-center gap-1.5 text-xs text-ink/50" data-id="element-277" data-mp="1flb6cyy6pntv"><UserRoundIcon className="h-3.5 w-3.5" data-id="element-278" data-mp="2iuxft3u44s15" />Account team</span>}</header>
          <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-5 py-6 sm:px-8" data-id="element-279" data-mp="2ix0esdd8tvi0">{activeThread.messages.map(message => <article key={message.id} className={`flex gap-3 ${message.author === 'ManyCo' ? 'justify-end' : ''}`} data-id="element-280" data-mp="2rvlwk6aw56hq">{message.author === 'Brand' && <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-ink/70 text-[10px] font-bold text-cream" data-id="element-281" data-mp="2nt7aldbxdqm8">{selectedBrand.name.slice(0, 2).toUpperCase()}</span>}<div className={`max-w-[88%] ${message.author === 'ManyCo' ? 'rounded-2xl rounded-tr-sm bg-cream px-4 py-3' : ''}`} data-id="element-282" data-mp="2wvgo8c2hq03u"><div className="mb-1 flex items-center gap-2 text-xs text-ink/45" data-id="element-283" data-mp="3bitay441abvt"><span className="font-semibold text-ink/75" data-id="element-284" data-mp="36krqbeqym4i8">{message.authorName}</span><span data-id="element-285" data-mp="36ljmorsds69k">{message.timestamp}</span></div><p className="text-sm leading-relaxed text-ink/80" data-id="element-286" data-mp="3kk9lcbuume01">{message.text}</p></div></article>)}</div>
          <form onSubmit={sendReply} className="border-t border-ink/10 p-4 sm:px-7" data-id="element-287" data-mp="3flpvi0d4wi2e"><div className="rounded-2xl border border-ink/15 bg-cream/55 p-2 focus-within:border-[#BB5E40]/40" data-id="element-288" data-mp="3fjljsw8zuerk"><label htmlFor="brand-message" className="sr-only" data-id="element-289" data-mp="3012riphlvb5">Reply to brand</label><textarea id="brand-message" value={reply} onChange={event => setReply(event.target.value)} placeholder={`Reply to ${selectedBrand.name}…`} rows={2} className="block w-full resize-none bg-transparent px-2 py-1 text-sm outline-none placeholder:text-ink/40" data-id="element-290" data-mp="80f0o7xdjovg" /><div className="flex items-center justify-between gap-3 px-1 pt-2" data-id="element-291" data-mp="3gg8f8h85aq9v"><button type="button" className="rounded p-1.5 text-ink/45 hover:bg-paper" aria-label="Attach a file" data-id="element-292" data-mp="3gexdso269ulx"><PaperclipIcon className="h-4 w-4" data-id="element-293" data-mp="3l9a0d6yy3m1w" /></button><button type="submit" disabled={!reply.trim()} className="inline-flex items-center gap-2 rounded-lg bg-ink px-3 py-2 text-xs font-semibold text-cream disabled:opacity-35" data-id="element-294" data-mp="36jlwpz3akbb0"><SendHorizontalIcon className="h-3.5 w-3.5" data-id="element-295" data-mp="2xi4jcoh9qnq0" />Send reply</button></div></div></form>
        </main>
      </div>
    </section>

    {newMessageOpen && <div className="fixed inset-0 z-50 flex items-end bg-ink/30 p-0 backdrop-blur-[1px] sm:items-center sm:justify-center sm:p-6" role="dialog" aria-modal="true" aria-labelledby="new-message-heading" data-id="element-296" data-mp="32gfv54r4j8av"><button type="button" className="absolute inset-0 cursor-default" onClick={() => setNewMessageOpen(false)} aria-label="Close start message" data-id="element-297" data-mp="3d0fazgmhngfg" /><section className="relative z-10 w-full max-w-xl rounded-t-3xl bg-paper p-5 shadow-2xl sm:rounded-3xl sm:p-7" data-id="element-298" data-mp="33z7qc1cwae3u"><div className="flex items-start justify-between gap-4" data-id="element-299" data-mp="2yzusklkhjmmh"><div data-id="element-300" data-mp="2z1pgm8r1zuus"><p className="text-xs font-semibold uppercase tracking-wider text-[#BB5E40]" data-id="element-301" data-mp="2z1fnwdemj9le">New conversation</p><h2 id="new-message-heading" className="mt-2 text-xl font-semibold" data-id="element-302" data-mp="2u83akzfeap7y">Message for {selectedBrand.name}</h2></div><button type="button" onClick={() => setNewMessageOpen(false)} className="rounded-lg p-2 text-ink/45 hover:bg-cream" aria-label="Close" data-id="element-303" data-mp="2p88kuz7cw5oh"><XIcon className="h-4 w-4" data-id="element-304" data-mp="2g9darc16ybfw" /></button></div><form onSubmit={startConversation} className="mt-6 space-y-5" data-id="element-305" data-mp="2g93n3v3mcgdd"><fieldset data-id="element-306" data-mp="2ba0ha8fte64c"><legend className="text-xs font-semibold text-ink/65" data-id="element-307" data-mp="2baa8u0giclc5">Send to</legend><div className="mt-2 grid gap-2 sm:grid-cols-3" data-id="element-308" data-mp="qshsjnc6k7rc">{contacts.map(contact => {
                const isCoverage = contact.id === selectedBrand.coverageRep;
                return <label key={contact.id} className={`relative cursor-pointer rounded-xl border p-3 transition-colors ${recipientId === contact.id ? 'border-[#BB5E40] bg-[#BB5E40]/5' : 'border-ink/10 hover:border-[#BB5E40]/30'} ${!contact.available ? 'opacity-55' : ''}`} data-id="element-309" data-mp="vqj8w0i81sz4"><input type="radio" name="recipient" value={contact.id} checked={recipientId === contact.id} onChange={() => contact.available && setRecipientId(contact.id)} disabled={!contact.available} className="sr-only" data-id="element-310" data-mp="82059ya4a4dd" /><span className={`flex h-7 w-7 items-center justify-center rounded-full text-[10px] font-bold text-cream ${contact.tone}`} data-id="element-311" data-mp="h2yaul07ejy2">{contact.initials}</span><span className="mt-2 block text-xs font-semibold" data-id="element-312" data-mp="luzi1siilkh8">{contact.name}</span><span className="mt-0.5 block text-[10px] leading-snug text-ink/50" data-id="element-313" data-mp="3kk6qcy7gl7pm">{contact.available ? contact.role : 'Away · unavailable'}</span>{isCoverage && <span className="mt-2 block text-[9px] font-semibold text-[#94452E]" data-id="element-314" data-mp="3pgeznuyz66ov">Default contact</span>}</label>;
              })}</div>{selectedBrand.coverageNote && <p className="mt-2 text-xs text-[#94452E]" data-id="element-315" data-mp="30y40yje33jii">{selectedBrand.coverageNote}</p>}</fieldset><div data-id="element-316" data-mp="35xqbgl2j2lnm"><label htmlFor="new-subject" className="mb-1.5 block text-xs font-semibold text-ink/65" data-id="element-317" data-mp="35xzu1nl8tqja">Subject</label><input id="new-subject" value={subject} onChange={event => setSubject(event.target.value)} placeholder="What would you like to discuss?" className="w-full rounded-xl border border-ink/15 bg-cream/45 px-3 py-2.5 text-sm outline-none focus:border-[#BB5E40]/40" data-id="element-318" data-mp="3awku9n0hwa7q" /></div><div data-id="element-319" data-mp="2i62vgy6t0v04"><label htmlFor="new-message" className="mb-1.5 block text-xs font-semibold text-ink/65" data-id="element-320" data-mp="2i5t2r2udk9qq">Message</label><textarea id="new-message" value={newMessage} onChange={event => setNewMessage(event.target.value)} placeholder="Write your message…" rows={4} className="w-full resize-none rounded-xl border border-ink/15 bg-cream/45 px-3 py-2.5 text-sm outline-none focus:border-[#BB5E40]/40" data-id="element-321" data-mp="2n5fgdde47s02" /></div><div className="flex justify-end gap-2" data-id="element-322" data-mp="1zfl4ay1me7gv"><button type="button" onClick={() => setNewMessageOpen(false)} className="rounded-lg px-3 py-2 text-xs font-semibold text-ink/60 hover:bg-cream" data-id="element-323" data-mp="1ryom88krhvhj">Cancel</button><button type="submit" disabled={!subject.trim() || !newMessage.trim()} className="inline-flex items-center gap-2 rounded-lg bg-[#BB5E40] px-3 py-2 text-xs font-semibold text-cream hover:bg-[#94452E] disabled:opacity-35" data-id="element-324" data-mp="1mwz8uma2h5qa"><SendHorizontalIcon className="h-3.5 w-3.5" data-id="element-325" data-mp="18zanldc37wkp" />Start message</button></div></form></section></div>}
  </div>;
}
function Toggle({
  checked,
  onChange,
  label
}: {
  checked: boolean;
  onChange: () => void;
  label: string;
}) {
  return <button type="button" role="switch" aria-checked={checked} onClick={onChange} aria-label={label} className={`flex h-5 w-9 shrink-0 items-center overflow-hidden rounded-full p-0.5 transition-colors ${checked ? 'bg-[#BB5E40]' : 'bg-ink/20'}`} data-id="element-326" data-mp="z8y65tzkkjis"><span className={`h-4 w-4 rounded-full bg-paper shadow-sm transition-[margin] ${checked ? 'ml-auto' : 'ml-0'}`} data-id="element-327" data-mp="pas1x5qaj1ar" /></button>;
}
function CategoryPill({
  category
}: {
  category: MessageCategory;
}) {
  const className = category === 'Action needed' ? 'bg-[#C9A04E]/15 text-[#8B6A20]' : category === 'Update' ? 'bg-forest/10 text-forest' : 'bg-[#BB5E40]/10 text-[#94452E]';
  return <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${className}`} data-id="element-328" data-mp="1oy8kavf9iyj">{category}</span>;
}